/**
 * The bottom nav — FEED · MATCH · RESULT · REWARDS.
 *
 * BK ruled this in on 27 Jul ("Bottom nav bar is what we should do"), replacing
 * the hamburger drawer as the app's primary navigation. The drawer stays for the
 * long tail (Help, Terms, VAR, prefs) — a four-tab bar that tries to hold nine
 * destinations stops being a nav bar.
 *
 * Four tabs, because the reference shows four and because a thumb reaching the
 * bottom of a phone can hit four targets reliably. Every tab points at a screen
 * that EXISTS — nothing here routes to a stub.
 */
import { useLocation } from "wouter";

export interface NavTab {
  label: string;
  href: string;
  /** Routes that should light this tab, beyond an exact href match. */
  matches?: readonly string[];
}

export const NAV_TABS: readonly NavTab[] = [
  { label: "FEED", href: "/feed", matches: ["/host", "/find-match"] },
  // MATCH is the live match centre. /m/:id is per match, so the TAP still routes
  // to the feed — the screen that knows which match is yours — but the tab now
  // LIGHTS on /m/ because the nav persists into the match centre (see
  // navSuppressedFor). Before that it could never light at all: FEED won the
  // longest-prefix contest on /feed, and /m was a route the nav never saw.
  { label: "MATCH", href: "/feed", matches: ["/m"] },
  { label: "RESULT", href: "/table", matches: ["/history", "/var"] },
  { label: "REWARDS", href: "/vault", matches: ["/cabinet", "/store", "/night-recap"] },
];

/** Which tab a path lights. Longest-prefix wins so /history beats a bare "/". */
export function activeTab(path: string, tabs: readonly NavTab[] = NAV_TABS): number {
  let best = -1;
  let bestLen = 0;
  tabs.forEach((tab, i) => {
    for (const p of [tab.href, ...(tab.matches ?? [])]) {
      if ((path === p || path.startsWith(`${p}/`)) && p.length > bestLen) {
        best = i;
        bestLen = p.length;
      }
    }
  });
  return best;
}

/**
 * NO `matchLive` DOT.
 *
 * The comp shows a green dot on MATCH while a match of yours is live, and I built
 * the prop for it — then never passed it, because the shell has no true
 * live-match signal. `loadParked` tracks beacon dismissal, not match state, and
 * the drawer's "LIVE" tag is a static label. So the prop defaulted to false and
 * the dot could never light: a feature that exists in the code and never in the
 * product, which is the exact bug this build keeps producing and which I have
 * flagged three times today in other people's work.
 *
 * Removed rather than left dark, and removed rather than wired to a fabricated
 * signal. It comes back when the shell can actually answer "is a match of mine
 * live" — which wants either a light poll in the shell or the match id parked
 * somewhere the chrome can read.
 */

/**
 * The four tab glyphs, drawn not generated.
 *
 * They do not exist in any handoff: the round-4 pack ships no icon assets and no
 * bottom nav at all — the nav comes from BK's later reference. So there was
 * nothing to wire, and a generic bordered square shipped in the meantime, which
 * is the placeholder-with-no-plan this build keeps producing.
 *
 * Authored as inline SVG for the same reason the airtime SIM icon was: at 18px a
 * monoline glyph lives or dies on whole-pixel geometry, and drawing beats
 * generating every time at this size. Inline rather than files so they inherit
 * `currentColor` — the active tab's volt and the inactive faint come from ONE
 * rule in CSS instead of two colourways per icon.
 *
 * 24-unit box, 1.75 stroke, round caps: matches the weight of the app's other
 * line work at the size it actually renders.
 */
export const ICON: Record<string, JSX.Element> = {
  // FEED — the card stack the feed IS.
  FEED: (
    <>
      <rect x="4" y="7" width="16" height="12" rx="2" />
      <path d="M7 4h10" />
    </>
  ),
  // MATCH — crossed play, the versus mark used across the product.
  MATCH: (
    <>
      <path d="M5 5l14 14" />
      <path d="M19 5L5 19" />
    </>
  ),
  // RESULT — a standings sheet: rows on a page.
  RESULT: (
    <>
      <rect x="5" y="3" width="14" height="18" rx="2" />
      <path d="M9 8h6M9 12h6M9 16h3" />
    </>
  ),
  // REWARDS — a cup on its base.
  REWARDS: (
    <>
      <path d="M7 4h10v5a5 5 0 0 1-10 0V4z" />
      <path d="M7 6H4.5a2.5 2.5 0 0 0 2.5 2.5M17 6h2.5A2.5 2.5 0 0 1 17 8.5" />
      {/* Stem lands ON the base (both y=19). Drawn at v4/y=20 first, which left
          a ~1.7px gap at render size and read as a floating plate. */}
      <path d="M12 14v5M9 19h6" />
    </>
  ),
};

function NavIcon({ label }: { label: string }) {
  return (
    <svg
      className="bn-ico"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      {ICON[label]}
    </svg>
  );
}

export interface BottomNavProps {
  /**
   * Returns true if the tap was swallowed (the result lock answered it), false
   * to navigate as normal. A guard rather than a `disabled` flag: the tabs must
   * still LOOK live and still respond, or a locked player reads the app as
   * broken instead of as asking them for something.
   */
  onIntercept?: () => boolean;
}

export function BottomNav({ onIntercept }: BottomNavProps = {}) {
  const [location, navigate] = useLocation();
  const active = activeTab(location);

  return (
    <nav className="bn-bar" aria-label="Primary">
      {NAV_TABS.map((tab, i) => (
        <button
          key={tab.label}
          type="button"
          className={`bn-tab${i === active ? " bn-tab--on" : ""}`}
          aria-current={i === active ? "page" : undefined}
          onClick={() => {
            if (onIntercept?.() === true) return;
            navigate(tab.href);
          }}
        >
          <NavIcon label={tab.label} />
          <span className="bn-label">{tab.label}</span>
        </button>
      ))}
    </nav>
  );
}
